import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useToast } from '@/hooks/use-toast';
import { uploadMedia } from '@/services/api/meta-media';
import {
  createBuilderItem, updateBuilderItem, submitBuilderItem,
  getBuilderApprovalStatus, confirmBuilderItem,
} from '@/services/api/template-builder';
import type { BuilderItem, BuilderVariable, BuilderButton } from '@/types/template-builder';

import MediaLibraryDialog from './MediaLibraryDialog';
import PageSelectDialog, { type PageOption } from './PageSelectDialog';
import TestSendDialog from './TestSendDialog';
import HighlightTextarea from './HighlightTextarea';
import {
  C, HATCH, HATCH_SM, MONO, card, label, input, smallInput, pill, dot, spinnerStyle, STATUS_META, varColor,
} from '../builderStyles';

export interface BuilderDraft {
  name: string; title: string; text_content: string; image_url: string | null;
  buttons: BuilderButton[]; variables: BuilderVariable[];
}
export function emptyDraft(): BuilderDraft {
  return { name: '', title: '', text_content: '', image_url: null, buttons: [], variables: [{ idx: 1, name: 'first_name', dynamic: true, example: 'Fulano', real: null }] };
}
export function itemToDraft(it: BuilderItem): BuilderDraft {
  return {
    name: it.name ?? '', title: it.title ?? '', text_content: it.text_content ?? '', image_url: it.image_url ?? null,
    buttons: (it.buttons ?? []).map((b) => ({ title: b.title, type: b.type ?? 'url', payload: b.payload })),
    variables: it.variables?.length ? it.variables : emptyDraft().variables,
  };
}

function renderBody(draft: BuilderDraft, mode: 'example' | 'real'): string {
  let text = draft.text_content || '';
  if (!text) return mode === 'example' ? 'Oi Fulano, 20% OFF pra você!' : 'Oi [nome do lead], [oferta ativa] pra você!';
  for (const v of draft.variables) {
    if (!v.name) continue;
    const value = mode === 'example' ? (v.example || v.name) : v.dynamic ? '[nome do lead]' : (v.real || '[' + v.name + ']');
    text = text.split('{{' + v.name + '}}').join(value);
  }
  return text;
}

const divider = { height: 1, background: C.lineSoft } as const;
const rowBox = { border: '1px solid ' + C.lineSoft, background: C.surfaceSoft, borderRadius: 12, padding: 11, display: 'flex', flexDirection: 'column', gap: 8 } as const;
const iconBtn = { border: 'none', background: '#f2f3f7', color: C.mutedSoft, width: 28, height: 28, flex: '0 0 28px', borderRadius: 8, fontSize: 13, cursor: 'pointer' } as const;
const addBtn = { border: '1px solid ' + C.field, background: C.surface, color: '#5b4ae0', borderRadius: 8, padding: '6px 11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' } as const;

interface Props {
  esteiraGroupId: number; esteiraName?: string; folderName?: string;
  folderId: number | null; projectId: number | null; pages: PageOption[];
  existingItem?: BuilderItem | null; onSaved: () => void;
  // Modo fila (wizard): "Colocar na fila" salva a peça e devolve o id pro pai
  // acumular; a página + submissão acontecem no fim, sem sair da tela.
  onQueued?: (id: number) => void;
  hasQueue?: boolean;  // já há itens na fila → esconde submeter/aprovar unitário
}

export default function BuilderWorkspace({ esteiraGroupId, esteiraName, folderName, folderId, projectId, pages, existingItem, onSaved, onQueued, hasQueue }: Props) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<BuilderDraft>(existingItem ? itemToDraft(existingItem) : emptyDraft());
  const [itemId, setItemId] = useState<number | null>(existingItem?.id ?? null);
  const [pageId, setPageId] = useState<string>(existingItem?.page_id ?? '');
  const [mode, setMode] = useState<'example' | 'real'>('example');
  const [mediaOpen, setMediaOpen] = useState(false);
  const [pageOpen, setPageOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  const patch = (p: Partial<BuilderDraft>) => setDraft((d) => ({ ...d, ...p }));
  // Ao digitar {{nova}} no texto, cria a variável automaticamente.
  // Ao digitar, só atualiza o texto. NÃO auto-cria variável tecla-a-tecla —
  // isso gerava {{t}},{{te}},{{tes}}... enquanto o usuário digitava {{teste}}.
  const onBodyChange = (text: string) => patch({ text_content: text });
  // A sincronização de variáveis novas do texto acontece quando o usuário SAI
  // do campo (blur): aí o {{...}} já está completo.
  const syncVarsFromText = () => {
    setDraft((d) => {
      const found = new Set<string>();
      const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(d.text_content)) !== null) found.add(m[1]);
      const existing = new Set(d.variables.map((v) => v.name));
      const add = [...found].filter((n) => !existing.has(n));
      if (!add.length) return d;
      const newVars: BuilderVariable[] = add.map((n, k) => ({
        idx: d.variables.length + k + 1, name: n,
        dynamic: n === 'first_name', example: n === 'first_name' ? 'Fulano' : '', real: n === 'first_name' ? null : '',
      }));
      return { ...d, variables: [...d.variables, ...newVars] };
    });
  };
  const upd = <T,>(arr: T[], i: number, p: Partial<T>) => arr.map((x, j) => (j === i ? { ...x, ...p } : x));
  const selectedPage = pages.find((p) => p.fb_page_id === pageId) ?? null;
  const uploadPageId = pageId || pages[0]?.fb_page_id || '';

  const { data: statusItem, refetch: refetchStatus } = useQuery({
    queryKey: ['builder', 'wstatus', itemId],
    queryFn: () => getBuilderApprovalStatus(itemId as number),
    enabled: !!itemId,
    refetchInterval: (q) => ((q.state.data as BuilderItem | undefined)?.approval_status === 'PENDING' ? 4000 : false),
  });
  const approval = statusItem?.approval_status ?? null;

  const validate = (needPage: boolean): string | null => {
    if (!draft.name.trim()) return 'Dê um nome interno à peça.';
    if (!draft.text_content.trim()) return 'Escreva a descrição.';
    for (const v of draft.variables) { if (!v.name.trim()) return 'Toda variável precisa de um nome.'; if (!v.dynamic && !(v.real ?? '').trim()) return `Preencha o valor real de {{${v.name}}}.`; }
    for (const b of draft.buttons) { if (!b.title.trim()) return 'O botão precisa de um texto.'; }
    if (needPage && !pageId) return 'Escolha a página do disparo.';
    return null;
  };
  const persist = async (): Promise<number> => {
    const common = { name: draft.name.trim(), title: draft.title || null, image_url: draft.image_url, text_content: draft.text_content, buttons: draft.buttons.map((b) => { const type = b.type ?? 'url'; const title = (b.title ?? '').trim(); return { title, type, payload: type === 'postback' ? title : null }; }), variables: draft.variables.map((v, i) => ({ ...v, idx: i + 1 })) };
    if (itemId) { await updateBuilderItem(itemId, { ...common, page_id: pageId }); return itemId; }
    const created = await createBuilderItem({ esteira_group_id: esteiraGroupId, page_id: pageId, folder_id: folderId, project_id: projectId, template_type: 'button', ...common });
    setItemId(created.id); return created.id;
  };
  const saveDraftMut = useMutation({ mutationFn: async () => { const e = validate(false); if (e) throw new Error(e); await persist(); }, onSuccess: () => { toast({ title: 'Salvo como rascunho' }); onSaved(); }, onError: (e: any) => toast({ title: 'Confira', description: String(e?.message ?? e), variant: 'destructive' }) });
  const submitMut = useMutation({ mutationFn: async () => { const e = validate(true); if (e) throw new Error(e); const id = await persist(); await submitBuilderItem(id); }, onSuccess: () => { refetchStatus(); }, onError: (e: any) => toast({ title: 'Erro ao submeter', description: String(e?.message ?? e), variant: 'destructive' }) });
  const confirmMut = useMutation({ mutationFn: async () => { const id = await persist(); await confirmBuilderItem(id); }, onSuccess: () => { toast({ title: 'Salvo já aprovado' }); onSaved(); }, onError: (e: any) => toast({ title: 'Não deu pra confirmar', description: String(e?.message ?? e), variant: 'destructive' }) });
  const queueMut = useMutation({ mutationFn: async () => { const e = validate(false); if (e) throw new Error(e); return await persist(); }, onSuccess: (id) => { toast({ title: 'Adicionada à fila' }); onQueued?.(id); }, onError: (e: any) => toast({ title: 'Confira', description: String(e?.message ?? e), variant: 'destructive' }) });

  // Avisa o RESULTADO da revisão (não o "enviei"): quando a Meta responde,
  // o painel já mostra, mas um toast garante que o usuário veja mesmo sem olhar.
  const prevApproval = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevApproval.current;
    if (prev && prev !== approval) {
      if (approval === 'APPROVED') toast({ title: 'Aprovado pela Meta ✓' });
      else if (approval === 'REJECTED') toast({ title: 'Reprovado pela Meta', description: statusItem?.rejection_reason || undefined, variant: 'destructive' });
      else if (approval === 'ERROR') toast({ title: 'Erro ao submeter à Meta', description: statusItem?.rejection_reason || undefined, variant: 'destructive' });
    }
    prevApproval.current = approval;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approval]);

  const genName = () => {
    const slug = (draft.title || draft.name || 'peca').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24) || 'peca';
    const hash = Math.random().toString(36).slice(2, 6);
    patch({ name: `${slug}-${hash}` });
  };

  const sending = submitMut.isPending || approval === 'PENDING';
  const busy = saveDraftMut.isPending || confirmMut.isPending || submitMut.isPending || queueMut.isPending;
  const uiStatus: keyof typeof STATUS_META = sending ? 'sending' : approval === 'APPROVED' ? 'approved' : (approval === 'REJECTED' || approval === 'ERROR') ? 'rejected' : 'draft';
  const st = STATUS_META[uiStatus];
  const varChips = useMemo(() => draft.variables.filter((v) => v.name.trim()), [draft.variables]);

  const onUpload = async (f?: File) => {
    if (!f) return;
    const pid = uploadPageId;
    if (!pid) { toast({ title: 'Escolha a página antes de subir mídia', variant: 'destructive' }); return; }
    try { const it = await uploadMedia({ file: f, page_id: pid }); if (it.file_url) patch({ image_url: it.file_url }); }
    catch (e: any) { toast({ title: 'Falha no upload', description: e?.response?.data?.detail || String(e?.message ?? e), variant: 'destructive' }); }
  };
  const brandBtn = (extra?: any) => ({ border: 'none', background: C.brand, color: '#fff', borderRadius: 11, padding: '13px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', ...extra });
  const ghostBtn = (extra?: any) => ({ border: '1px solid ' + C.field, background: C.surface, color: C.inkSoft, borderRadius: 11, padding: '12px 16px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', ...extra });

  return (
    <div style={{ display: 'flex', gap: 22, alignItems: 'flex-start' }}>
      {/* ══ LEFT — form em 2 colunas ══ */}
      <section style={{ ...card, flex: '1 1 auto', minWidth: 0, padding: 20, display: 'flex', flexDirection: 'column', gap: 13 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11.5 }}>
          <span style={{ fontFamily: MONO, color: '#5b4ae0', background: C.brandSoft, borderRadius: 6, padding: '4px 8px' }}>{esteiraName || '—'}</span>
          <span style={{ color: '#c3c8d4' }}>→</span>
          <span style={{ fontFamily: MONO, color: '#5b4ae0', background: C.brandSoft, borderRadius: 6, padding: '4px 8px' }}>{folderName || 'Raiz'}</span>
        </div>

        <div style={{ display: 'flex', gap: 22, alignItems: 'flex-start' }}>
        {/* — coluna A: nome, mídia, título, descrição — */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 13 }}>
        <Field lbl="Nome da peça" hint="Interno. Não aparece pro lead.">
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={draft.name} onChange={(e) => patch({ name: e.target.value })} placeholder="ex: oferta-carro-setembro" style={{ ...input, fontWeight: 600, flex: 1, minWidth: 0 }} />
            <button type="button" onClick={genName} title="Gerar nome (título + hash)" style={{ ...ghostBtn({ padding: '0 13px', fontSize: 12.5, whiteSpace: 'nowrap' }) }}>⚡ Gerar</button>
          </div>
        </Field>

        <div style={divider} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <label style={label}>Mídia do card</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setMediaOpen(true)} style={{ flex: 1, border: '1px solid ' + C.brand, background: C.brand, color: '#fff', borderRadius: 10, padding: '11px 12px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>Selecionar da biblioteca</button>
            <label style={{ border: '1px solid ' + C.field, background: C.surface, color: C.inkSoft, borderRadius: 10, padding: '11px 14px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
              Enviar foto
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => onUpload(e.target.files?.[0] || undefined)} />
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, border: '1px solid ' + C.lineSoft, background: C.surfaceSoft, borderRadius: 12, padding: 10 }}>
            {draft.image_url ? <img src={draft.image_url} alt="" style={{ width: 46, height: 46, borderRadius: 9, objectFit: 'cover' }} /> : <span style={{ width: 46, height: 46, flex: '0 0 46px', borderRadius: 9, background: HATCH_SM }} />}
            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>{draft.image_url ? 'Mídia selecionada' : 'Nenhuma mídia'}</span>
              <span style={{ fontSize: 11.5, color: C.mutedSoft }}>Opcional · aparece no topo do card</span>
            </div>
            {draft.image_url && <button onClick={() => patch({ image_url: null })} style={{ ...iconBtn, marginLeft: 'auto' }}>✕</button>}
          </div>
        </div>

        <div style={divider} />

        <Field lbl="Título do card">
          <input value={draft.title} onChange={(e) => patch({ title: e.target.value })} placeholder="ex: Novidade pra você" style={{ ...input, fontWeight: 600 }} />
        </Field>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <label style={label}>Descrição</label>
          <HighlightTextarea value={draft.text_content} onChange={onBodyChange} onBlur={syncVarsFromText} rows={6} placeholder="Oi {{first_name}}, {{oferta}} pra você!" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11.5, color: C.mutedSoft, fontWeight: 600 }}>Inserir:</span>
            {varChips.map((v) => {
              const c = varColor(v.name);
              return (
                <button key={v.name} onClick={() => onBodyChange((draft.text_content || '') + '{{' + v.name + '}}')} style={{ fontFamily: MONO, fontSize: 11.5, color: c.fg, background: c.bg, border: '1px solid ' + c.bg, borderRadius: 7, padding: '4px 8px', cursor: 'pointer' }}>{'{{' + v.name + '}}'}</button>
              );
            })}
          </div>
        </div>

        </div>{/* fim coluna A */}

        {/* — coluna B: variáveis, botão — */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 13 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <label style={label}>Variáveis</label>
            <button onClick={() => patch({ variables: [...draft.variables, { idx: draft.variables.length + 1, name: '', dynamic: false, example: '', real: '' }] })} style={addBtn}>+ Variável</button>
          </div>
          {draft.variables.map((v, i) => (
            <div key={i} style={rowBox}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: MONO, fontSize: 11, background: varColor(v.name || '').bg, color: varColor(v.name || '').fg, borderRadius: 5, padding: '2px 6px' }}>{'{{' + (v.name || '…') + '}}'}</span>
                <input value={v.name} disabled={v.dynamic} onChange={(e) => patch({ variables: upd(draft.variables, i, { name: e.target.value }) })} placeholder="nome_da_variavel" style={{ ...smallInput, flex: 1, minWidth: 0, fontFamily: MONO, fontSize: 12 }} />
                {!v.dynamic && <button onClick={() => patch({ variables: draft.variables.filter((_, j) => j !== i) })} style={iconBtn}>✕</button>}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input value={v.example} onChange={(e) => patch({ variables: upd(draft.variables, i, { example: e.target.value }) })} placeholder="exemplo p/ Meta" style={{ ...smallInput, flex: '1 1 120px', minWidth: 0 }} />
                {v.dynamic ? (
                  <div style={{ ...smallInput, flex: '1 1 130px', minWidth: 0, display: 'flex', alignItems: 'center', border: '1px solid #cdeadd', background: '#f2fbf7', color: C.greenDark, fontWeight: 600 }}>nome do lead</div>
                ) : (
                  <input value={v.real ?? ''} onChange={(e) => patch({ variables: upd(draft.variables, i, { real: e.target.value }) })} placeholder="valor real (lead)" style={{ ...smallInput, flex: '1 1 130px', minWidth: 0 }} />
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={divider} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <label style={label}>Botão</label>
            {draft.buttons.length === 0 && (
              <button onClick={() => patch({ buttons: [{ title: '', type: 'url' }] })} style={addBtn}>+ Botão</button>
            )}
          </div>
          {draft.buttons.length === 0 && <span style={{ fontSize: 12, color: C.faint }}>Sem botões. URL usa o link rastreado da esteira; Postback envia um payload pro webhook no clique.</span>}
          {draft.buttons.map((b, i) => {
            const btype = b.type ?? 'url';
            return (
              <div key={i} style={{ ...rowBox, flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <select value={btype} onChange={(e) => patch({ buttons: upd(draft.buttons, i, { type: e.target.value as any, payload: null }) })} style={{ ...smallInput, flex: '0 0 118px', fontWeight: 600 }}>
                    <option value="url">URL</option>
                    <option value="postback">Postback</option>
                  </select>
                  <input value={b.title} onChange={(e) => patch({ buttons: upd(draft.buttons, i, { title: e.target.value }) })} placeholder="Texto do botão" style={{ ...smallInput, flex: 1, minWidth: 0 }} />
                  <button onClick={() => patch({ buttons: draft.buttons.filter((_, j) => j !== i) })} style={iconBtn}>✕</button>
                </div>
                <span style={{ fontSize: 11.5, color: C.faint }}>
                  {btype === 'postback' ? 'Postback — o payload enviado ao webhook é o próprio texto do botão.' : 'A URL vem da esteira (link rastreado).'}
                </span>
              </div>
            );
          })}
        </div>
        </div>{/* fim coluna B */}
        </div>{/* fim 2 colunas */}
      </section>

      {/* ══ RIGHT — preview + aprovação ══ */}
      <section style={{ flex: '0 0 380px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Prévia */}
        <div style={{ ...card, padding: '16px 20px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 14, flexWrap: 'wrap', rowGap: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.01em' }}>Prévia</span>
            <div style={{ display: 'inline-flex', background: '#f2f3f7', borderRadius: 10, padding: 3 }}>
              {(['example', 'real'] as const).map((m) => (
                <button key={m} onClick={() => setMode(m)} style={{ border: 'none', background: mode === m ? C.surface : 'transparent', color: mode === m ? C.ink : '#7b8296', borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{m === 'example' ? 'Exemplo (Meta)' : 'Real (lead)'}</button>
              ))}
            </div>
          </div>
          <div style={{ background: '#e9edf2', borderRadius: 14, padding: '18px 24px', display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 420, display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <span style={{ width: 30, height: 30, flex: '0 0 30px', borderRadius: 999, background: C.brand }} />
              <div style={{ flex: 1, minWidth: 0, background: C.surface, borderRadius: '16px 16px 16px 4px', boxShadow: '0 2px 10px rgba(16,22,40,.08)', overflow: 'hidden' }}>
                {draft.image_url ? (
                  <img src={draft.image_url} alt="" style={{ width: '100%', height: 148, objectFit: 'cover', display: 'block' }} />
                ) : (
                  <div style={{ height: 148, background: HATCH, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontFamily: MONO, fontSize: 11.5, color: C.mutedSoft, background: 'rgba(255,255,255,.85)', padding: '5px 10px', borderRadius: 6 }}>Nenhuma mídia</span>
                  </div>
                )}
                <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 7 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-.01em', color: draft.title ? C.ink : '#b0b6c4' }}>{draft.title || 'Título do card'}</div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, color: draft.text_content ? C.inkSoft : '#98a0b0', whiteSpace: 'pre-wrap' }}>{renderBody(draft, mode)}</div>
                  <div style={{ fontSize: 11, color: '#b0b6c4', textAlign: 'right' }}>14:32</div>
                </div>
                {draft.buttons.map((b, i) => (
                  <div key={i} style={{ borderTop: '1px solid ' + C.lineSoft, padding: 11, textAlign: 'center', fontSize: 13.5, fontWeight: 700, color: '#1f7ae0' }}>{b.title || 'Texto do botão'}</div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center', fontSize: 12, color: C.mutedSoft, marginTop: 12 }}>{mode === 'example' ? 'Valores de exemplo enviados p/ Meta' : 'Simulação com dados de um lead real'}</div>
        </div>

        {/* Modo fila com itens: só "colocar na fila" — envio é coletivo no fim,
            então esconde o Submeter/Salvar já aprovado unitários (evita confundir). */}
        {onQueued && hasQueue && (
          <div style={{ ...card, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.01em' }}>Aprovação em lote</span>
            <span style={{ fontSize: 12.5, lineHeight: 1.5, color: C.mutedSoft }}>Já há itens na fila abaixo. Adicione esta peça e envie todas de uma vez pela fila.</span>
            <button onClick={() => queueMut.mutate()} disabled={busy} style={brandBtn({ width: '100%' })}>{itemId ? '✓ Salvar na fila' : '+ Colocar na fila'}</button>
            <button onClick={() => saveDraftMut.mutate()} disabled={busy} style={ghostBtn({ width: '100%' })}>Salvar como rascunho</button>
          </div>
        )}

        {/* Aprovação & envio (peça única — some quando a fila já tem itens) */}
        {!(onQueued && hasQueue) && (
        <div style={{ ...card, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', rowGap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.01em' }}>Aprovação &amp; envio</span>
            <span style={pill(st.bg, st.fg)}>{st.label}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid ' + C.lineSoft, background: C.surfaceSoft, borderRadius: 12, padding: '11px 13px' }}>
            <span style={{ width: 34, height: 34, flex: '0 0 34px', borderRadius: 999, background: HATCH_SM }} />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>{selectedPage?.page_name ?? (pageId || 'Nenhuma página escolhida')}</span>
              <span style={{ fontSize: 11.5, color: C.mutedSoft }}>{selectedPage ? 'token ativo' : 'escolha a página do disparo'}</span>
            </div>
            <button onClick={() => setPageOpen(true)} style={{ border: '1px solid ' + C.field, background: C.surface, color: C.inkSoft, borderRadius: 9, padding: '8px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{selectedPage ? 'Trocar' : 'Escolher'}</button>
          </div>

          {sending ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 13, border: '1px solid #e0dcfb', background: '#f7f6ff', borderRadius: 12, padding: 16 }}>
              <span className="animate-spin" style={spinnerStyle()} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: '#4535c9' }}>Enviando para a Meta…</span>
                <span style={{ fontSize: 12, color: '#7b74a8' }}>Aguardando retorno da revisão</span>
              </div>
            </div>
          ) : uiStatus === 'approved' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13, border: '1px solid #c9ecdd', background: '#f2fbf7', borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span style={dot(C.green)}>✓</span><span style={{ fontSize: 13.5, fontWeight: 700, color: C.greenDark }}>Aprovado pela Meta{selectedPage ? ` em ${selectedPage.page_name}` : ''}</span></div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => setTestOpen(true)} style={{ border: 'none', background: C.green, color: '#fff', borderRadius: 10, padding: '11px 16px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>Submeter envio de teste</button>
                <button onClick={() => submitMut.mutate()} disabled={busy} style={ghostBtn({ padding: '11px 14px' })}>Reenviar à Meta</button>
              </div>
            </div>
          ) : uiStatus === 'rejected' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, border: '1px solid #f6d3d3', background: '#fdf5f5', borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span style={dot('#e05252')}>!</span><span style={{ fontSize: 13.5, fontWeight: 700, color: C.red }}>{approval === 'ERROR' ? 'Erro ao submeter à Meta' : 'Reprovado pela Meta'}</span></div>
              {statusItem?.rejection_reason && <span style={{ fontSize: 12.5, lineHeight: 1.5, color: '#8a4444' }}>Motivo: {statusItem.rejection_reason}</span>}
              <button onClick={() => submitMut.mutate()} disabled={busy} style={{ alignSelf: 'flex-start', border: 'none', background: C.brand, color: '#fff', borderRadius: 10, padding: '11px 16px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>Reenviar para a Meta</button>
            </div>
          ) : (
            <button onClick={() => submitMut.mutate()} disabled={busy} style={brandBtn()}>Submeter para a Meta</button>
          )}

          <div style={divider} />
          {onQueued && (
            <button onClick={() => queueMut.mutate()} disabled={busy} style={brandBtn({ width: '100%' })}>{itemId ? '✓ Salvar na fila' : '+ Colocar na fila'}</button>
          )}
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <button onClick={() => saveDraftMut.mutate()} disabled={busy} style={ghostBtn({ flex: '1 1 160px' })}>Salvar como rascunho</button>
            <button onClick={() => confirmMut.mutate()} disabled={busy || approval !== 'APPROVED'} style={{ flex: '1 1 160px', border: '1px solid ' + (approval === 'APPROVED' ? C.green : C.line), background: approval === 'APPROVED' ? C.green : C.bg, color: approval === 'APPROVED' ? '#fff' : C.faint, borderRadius: 11, padding: '12px 16px', fontSize: 13.5, fontWeight: 700, cursor: approval === 'APPROVED' ? 'pointer' : 'not-allowed' }}>Salvar já aprovado</button>
          </div>
        </div>
        )}
      </section>

      <MediaLibraryDialog open={mediaOpen} onOpenChange={setMediaOpen} pageId={uploadPageId} onSelect={(url) => patch({ image_url: url })} />
      <PageSelectDialog open={pageOpen} onOpenChange={setPageOpen} esteiraName={esteiraName} pages={pages} onSelect={(fb) => setPageId(fb)} />
      {itemId && <TestSendDialog open={testOpen} onOpenChange={setTestOpen} itemId={itemId} pageId={pageId} onSuccess={() => refetchStatus()} />}
    </div>
  );
}

function Field({ lbl, hint, children }: { lbl: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <label style={label}>{lbl}</label>
      {children}
      {hint && <span style={{ fontSize: 11.5, color: C.faint }}>{hint}</span>}
    </div>
  );
}
